/**
 * Frota · Manutenção · Grupos de veículos.
 *
 * Grupo = nome, descrição e as PLACAS escolhidas (o modelo do caminhão não é
 * critério). As placas do grupo filtram o plano preventivo, o painel de gestão e
 * as O.S.; com plano próprio, ele vira o plano de cada placa.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceVehicleGroupListDto } from "@bi/types";
import { api } from "../lib/api";
import { fmtNumber } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { CARD_GRID, TableOrCards, useMobile } from "../features/maintenance-mobile/mobile-ui";
import { IconChevronRight } from "../components/icons";

export function MaintenanceVehicleGroupsPage() {
  const mobile = useMobile();
  const { can } = useCapabilities();
  const { data, isLoading } = useQuery({
    queryKey: ["maintenance", "vehicle-groups"],
    queryFn: () => api.get<MaintenanceVehicleGroupListDto>("/maintenance/vehicle-groups"),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Manutenção · Grupos de veículos"
        subtitle="Organize a frota em grupos de placas (ex.: Frota Transbordo). As placas do grupo filtram o plano preventivo, o painel e as O.S., e o grupo pode ter plano próprio."
        actions={
          <span className="flex gap-2">
            <Link className="btn-secondary" to="/frota/manutencao/preventiva">Plano preventivo</Link>
            {can("workorder.manage") && <Link className="btn-primary" to="/frota/manutencao/grupos/novo">Novo grupo</Link>}
          </span>
        }
      />

      {data && (
        <div className="card p-4 text-sm text-gray-600">
          {fmtNumber(data.fleetSize - data.vehiclesWithoutGroup)} de {fmtNumber(data.fleetSize)} placas da frota própria estão em algum grupo.
          {data.vehiclesWithoutGroup > 0 && <> As {fmtNumber(data.vehiclesWithoutGroup)} restantes seguem o intervalo do ERP e o plano padrão da planilha.</>}
        </div>
      )}

      <div className={mobile ? "" : "card overflow-x-auto"}>
        <TableOrCards
          cards={
            <div className={CARD_GRID}>
              {isLoading && <p className="card p-4 text-sm text-gray-500">Carregando…</p>}
              {data?.groups.length === 0 && <p className="card p-4 text-sm text-gray-500">Nenhum grupo ainda. Crie o primeiro com nome, descrição e as placas.</p>}
              {data?.groups.map((g) => (
                <Link key={g.id} to={`/frota/manutencao/grupos/${g.id}`} className={`card block p-3.5 active:bg-surface-muted ${g.active ? "" : "opacity-60"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0 text-sm font-semibold text-gray-900">{g.name}</span>
                    <span className={g.active ? "badge-green" : "badge-gray"}>{g.active ? "Ativo" : "Desativado"}</span>
                  </div>
                  {g.description && <p className="mt-0.5 text-xs text-gray-500">{g.description}</p>}
                  <p className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
                    <span className="flex-1">
                      {fmtNumber(g.vehicleCount ?? 0)} {g.vehicleCount === 1 ? "placa" : "placas"} · {g.planItemCount ? `${g.planItemCount} itens no plano` : "sem plano"}
                    </span>
                    <IconChevronRight className="h-4 w-4 text-gray-400" />
                  </p>
                </Link>
              ))}
            </div>
          }
          table={
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-xs text-gray-500">
            <tr>
              <th className="text-left px-4 py-2">Grupo</th>
              <th className="text-right px-4 py-2">Placas</th>
              <th className="text-right px-4 py-2">Itens do plano</th>
              <th className="text-left px-4 py-2">Situação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && <tr><td className="px-4 py-6 text-gray-500" colSpan={4}>Carregando…</td></tr>}
            {data?.groups.length === 0 && (
              <tr><td className="px-4 py-6 text-gray-500" colSpan={4}>Nenhum grupo ainda. Crie o primeiro com nome, descrição e as placas.</td></tr>
            )}
            {data?.groups.map((g) => (
              <tr key={g.id} className={g.active ? "" : "text-gray-400"}>
                <td className="px-4 py-2">
                  <Link className="font-medium text-tm-600 hover:underline" to={`/frota/manutencao/grupos/${g.id}`}>{g.name}</Link>
                  {g.description && <div className="text-xs text-gray-400">{g.description}</div>}
                </td>
                <td className="px-4 py-2 text-right">
                  <Link className="hover:underline" to={`/frota/manutencao/preventiva?group=${g.id}`}>{g.vehicleCount ?? 0}</Link>
                </td>
                <td className="px-4 py-2 text-right">{g.planItemCount ? g.planItemCount : <span className="text-gray-400">sem plano</span>}</td>
                <td className="px-4 py-2"><span className={g.active ? "badge-green" : "badge-gray"}>{g.active ? "Ativo" : "Desativado"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
          }
        />
      </div>
    </div>
  );
}
